<template>
  <div
    class="fpui-input-checkbox-tree"
    :class="{ disabled }"
    :style="{ '--checkbox-tree-columns': gridColumns }"
  >
    <div
      v-if="allLabel || columns.length"
      class="checkbox-tree-row checkbox-tree-header"
    >
      <div class="checkbox-tree-cell checkbox-tree-main">
        <fpui-input-checkbox
          v-if="allLabel"
          :value="allState === CHECKED"
          :indeterminate="allState === PARTIAL"
          :disabled="disabled || !nodes.length"
          @input="onToggleAll"
        >
          {{ allLabel }}
        </fpui-input-checkbox>
      </div>
      <div
        v-for="column in columns"
        :key="column.key"
        class="checkbox-tree-cell"
      >
        <slot
          name="header-cell"
          :column="column"
        >
          {{ column.label }}
        </slot>
      </div>
    </div>

    <div
      v-for="row in rows"
      :key="row.node.id"
      class="checkbox-tree-row"
      :data-testid="testId ? `${testId}-${row.node.id}` : null"
    >
      <div
        class="checkbox-tree-cell checkbox-tree-main"
        :style="{ '--checkbox-tree-depth': row.depth }"
      >
        <button
          v-if="isExpandable(row.node)"
          type="button"
          class="checkbox-tree-toggle"
          :aria-expanded="String(isOpen(row.node))"
          :aria-label="$t(isOpen(row.node) ? 'checkbox_tree.collapse' : 'checkbox_tree.expand', { label: row.node.label })"
          @click="toggleOpen(row.node)"
        >
          <i
            class="fp4"
            :class="isOpen(row.node) ? 'fp4-angle-down' : 'fp4-angle-right'"
          />
        </button>
        <span
          v-else
          class="checkbox-tree-toggle"
        />
        <fpui-input-checkbox
          :value="states.get(row.node.id) === CHECKED"
          :indeterminate="states.get(row.node.id) === PARTIAL"
          :disabled="disabled"
          @input="onToggle(row.node)"
        >
          <slot
            name="label"
            :node="row.node"
          >
            {{ row.node.label }}
          </slot>
        </fpui-input-checkbox>
        <fp-loading
          v-if="loadingIds.includes(row.node.id)"
          :size="14"
        />
      </div>
      <div
        v-for="column in columns"
        :key="column.key"
        class="checkbox-tree-cell"
      >
        <slot
          name="cell"
          :node="row.node"
          :column="column"
          :state="states.get(row.node.id)"
          :depth="row.depth"
        />
      </div>
    </div>
  </div>
</template>

<script>
// A tree of checkboxes. A parent shows a dash when only some of its children are checked.
// The value is the list of checked ids; a parent id stands for the parent and all its
// children, even those not loaded yet (see CheckboxTree.js).
import FpLoading from '@/shared/components/loader/FpLoading'
import { CHECKED, PARTIAL, computeStates, isExpandable, rootState, toggle, toggleAll, visibleRows } from './CheckboxTree'

export default {
  name: 'FpuiInputCheckboxTree',
  components: { FpLoading },
  props: {
    value: { type: Array, default: () => [] },
    // { id, label, children, lazy }. `lazy` with no `children` yet: the node can be
    // opened, and the parent loads its children on the `expand` event.
    nodes: { type: Array, default: () => [] },
    // Text of the box above the tree that checks every node. No box when empty.
    allLabel: { type: String, default: '' },
    // Extra cells on each row: { key, label, width }. Filled by the `cell` slot.
    columns: { type: Array, default: () => [] },
    // Ids of the nodes whose children are loading.
    loadingIds: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    // Each row gets data-testid="<testId>-<node id>".
    testId: { type: String, default: '' }
  },
  emits: ['input', 'expand'],
  data () {
    return {
      CHECKED,
      PARTIAL,
      opened: []
    }
  },
  computed: {
    states () {
      return computeStates(this.nodes, this.value)
    },
    allState () {
      return rootState(this.nodes, this.value)
    },
    rows () {
      return visibleRows(this.nodes, new Set(this.opened))
    },
    gridColumns () {
      return ['minmax(0, 1fr)', ...this.columns.map(column => column.width || '120px')].join(' ')
    }
  },
  methods: {
    isExpandable,
    isOpen (node) {
      return this.opened.includes(node.id)
    },
    toggleOpen (node) {
      if (this.isOpen(node)) {
        this.opened = this.opened.filter(id => id !== node.id)
        return
      }
      this.opened = [...this.opened, node.id]
      this.$emit('expand', node)
    },
    onToggle (node) {
      if (this.disabled) return
      this.$emit('input', toggle(this.nodes, this.value, node.id))
    },
    onToggleAll () {
      if (this.disabled) return
      this.$emit('input', toggleAll(this.nodes, this.value))
    }
  }
}
</script>

<style lang="less">
@import "~@/shared/styles/_variables.less";

.fpui-input-checkbox-tree {
  font-size: 14px;
  color: @text-color;
  .checkbox-tree-row {
    display: grid;
    grid-template-columns: var(--checkbox-tree-columns);
    align-items: center;
    min-height: 36px;
    border-bottom: 1px solid @grey_dark;
  }
  .checkbox-tree-header {
    font-size: 12px;
    font-weight: 600;
    color: @label-color;
  }
  .checkbox-tree-cell {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    padding: 0 8px;
  }
  .checkbox-tree-main {
    padding-left: calc(var(--checkbox-tree-depth, 0) * 24px);
  }
  .checkbox-tree-toggle {
    flex: 0 0 24px;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    background: none;
    color: @grey;
    cursor: pointer;
    &:hover, &:focus-visible {
      color: @blue;
    }
  }
  span.checkbox-tree-toggle {
    cursor: default;
  }
  // The checkbox brings its own margins for inline forms: none inside a row.
  .fpui-input-checkbox {
    min-width: 0;
    margin: 0;
    .fp-checkbox {
      top: 0;
      height: auto;
      display: flex;
      align-items: center;
    }
    .content-checkbox {
      min-width: 0;
    }
  }
  &.disabled .checkbox-tree-toggle {
    cursor: not-allowed;
  }
}
</style>
